import React from "react";
import {
  useConsultOrderContext,
  lineUnitPrice,
  estimatedItemsSubtotal,
} from "../../contexts/ConsultOrderContext";
import { ItemBasicResponse } from "../../types/medicineSearch.types";
import styles from "./OrderItemsList.module.scss";

// ITAM-2583 — the current order DRAFT lines, read straight from the shared
// context. Each line has a quantity stepper clamped to [1, stock], a remove
// button, and a per-line price computed from the DISCOUNTED branch unit price.
// The only prop is an optional map of server-reported per-line errors keyed by
// itemId (surfaced on a 409 out-of-stock, etc.).

const IMAGE_SIZE = 40;
const CDN_BASE = process.env.REACT_APP_CDN_PATH ?? "";

const firstImageUrl = (basicInfo: ItemBasicResponse): string | undefined => {
  const image = basicInfo?.files?.find((file) => file.type === "IMAGE");
  return image ? `${CDN_BASE}${image.location}` : undefined;
};

const formatPrice = (value: number): string => `${value} RSD`;

interface OrderItemsListProps {
  lineErrors?: Record<number, string>;
}

const LineImage = ({ src }: { src: string }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return (
      <span
        className={styles.thumbFallback}
        data-testid="order-line-image-fallback"
        aria-hidden="true"
      />
    );
  }
  return (
    <img
      className={styles.thumb}
      src={src}
      alt=""
      width={IMAGE_SIZE}
      height={IMAGE_SIZE}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const OrderItemsList: React.FC<OrderItemsListProps> = ({ lineErrors }) => {
  const { draft, setItemQuantity, removeItem } = useConsultOrderContext();
  const { items } = draft;

  if (items.length === 0) {
    return (
      <div className={styles.OrderItemsList}>
        <p className={styles.empty}>Nema dodatih artikala.</p>
      </div>
    );
  }

  return (
    <div className={styles.OrderItemsList}>
      <ul className={styles.list}>
        {items.map((line) => {
          const { item, quantity } = line;
          const itemId = item.id.itemId;
          const imageUrl = firstImageUrl(item.basicInfo);
          const unitPrice = lineUnitPrice(item);
          const atMin = quantity <= 1;
          const atMax = quantity >= item.quantity;
          const lineError = lineErrors?.[itemId];
          const qtyInputId = `order-line-qty-${itemId}`;

          return (
            <li key={itemId} className={styles.line}>
              <div className={styles.lineMainRow}>
                {imageUrl ? (
                  <LineImage src={imageUrl} />
                ) : (
                  <span className={styles.thumbFallback} aria-hidden="true" />
                )}
                <div className={styles.lineInfo}>
                  <span className={styles.lineName}>{item.name}</span>
                  {item.itemExternalName && (
                    <span className={styles.lineExternal}>
                      {item.itemExternalName}
                    </span>
                  )}
                </div>

                <div className={styles.stepper}>
                  <button
                    type="button"
                    className={styles.stepBtn}
                    aria-label={`Smanji količinu za ${item.name}`}
                    disabled={atMin}
                    onClick={() => setItemQuantity(itemId, quantity - 1)}
                  >
                    −
                  </button>
                  <label className={styles.srOnly} htmlFor={qtyInputId}>
                    Količina za {item.name}
                  </label>
                  <input
                    id={qtyInputId}
                    className={styles.qtyInput}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={item.quantity}
                    value={quantity}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      if (!Number.isNaN(parsed)) {
                        setItemQuantity(itemId, parsed);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={styles.stepBtn}
                    aria-label={`Povećaj količinu za ${item.name}`}
                    disabled={atMax}
                    onClick={() => setItemQuantity(itemId, quantity + 1)}
                  >
                    +
                  </button>
                </div>

                <span className={styles.linePrice}>
                  {formatPrice(unitPrice * quantity)}
                </span>

                <button
                  type="button"
                  className={styles.removeBtn}
                  aria-label={`Ukloni ${item.name}`}
                  onClick={() => removeItem(itemId)}
                >
                  ×
                </button>
              </div>

              {lineError && (
                <p className={styles.lineError} role="alert">
                  {lineError}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <div className={styles.footer}>
        <span className={styles.footerLabel}>Procenjena međusuma:</span>
        <span className={styles.footerValue}>
          {formatPrice(estimatedItemsSubtotal(items))}
        </span>
      </div>
    </div>
  );
};

export default OrderItemsList;
